import { useEffect, useMemo, useState } from "react";
import {
  Download, Loader2, CheckCircle2, AlertCircle, Plus, X, ChevronUp, ChevronDown,
} from "lucide-react";
import { takeOfferPrefill } from "@/lib/offerPrefill";

// Internal Markdown document generator (M1 / issue #8).
//
// The page is protected by Caddy Basic Auth on /internal/* (so the
// browser already prompted before this React code ran). The API on
// api.<DOMAIN> is also behind Caddy Basic Auth, but that auth scope
// is a different origin — the browser does NOT replay the SPA-host
// credentials there. We therefore prompt for THREE values up-front
// and include both an explicit Authorization: Basic header (for
// Caddy on the api vhost) and an X-RiskaiA-Token header (for the
// backend's app-token check) on every API call.
//
// Styling follows the Compliance Assessment Sjabloon page (pages/Sjabloon.jsx):
// the app's dark navy theme — rk-container layout, overline labels, Fraunces
// heading, transparent underline inputs with light text, rk-btn-primary.

const STORAGE_KEY = "riskaia.internal.creds.v1";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Shared field styling, matching pages/Sjabloon.jsx.
const inputCls =
  "w-full bg-transparent border-b border-[#C0C5CE]/40 focus:border-[#C0C5CE] py-2 text-white outline-none";
const areaCls =
  "w-full bg-transparent border border-[#C0C5CE]/40 focus:border-[#C0C5CE] p-4 text-[#D4D8DF] outline-none resize-y leading-relaxed";

function loadCreds() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || typeof c !== "object") return null;
    if (!c.user || !c.pass || !c.token) return null;
    return c;
  } catch {
    return null;
  }
}

// Default form values for a template's document fields.
function fieldDefaults(tpl) {
  return Object.fromEntries((tpl?.fields || []).map((f) => [f.key, f.default ?? ""]));
}

export default function InternalDocuments() {
  const [creds, setCreds] = useState(() => loadCreds());
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [markdown, setMarkdown] = useState("");
  // Document fields (the YAML preamble) — filled here, built into the
  // preamble server-side. Keyed by field.key; initialised from defaults.
  const [fieldValues, setFieldValues] = useState({});
  // Signatory form for templates with a signature block (template.signatures).
  // Roles are prefilled; empty fields keep the driver's placeholder default.
  const [signatories, setSignatories] = useState({
    one_role: "Namens RiskaiA B.V.",
    one_name: "",
    two_role: "Namens de inkooporganisatie",
    two_name: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [successUrl, setSuccessUrl] = useState("");

  // Two headers on every API call:
  //   Authorization: Basic <base64(user:pass)>   — for Caddy basicauth on api.<DOMAIN>
  //   X-RiskaiA-Token: <token>                   — for the backend app-token check
  // The browser's cached basicauth for app.<DOMAIN> does not carry to
  // api.<DOMAIN>; we send it ourselves.
  const authHeaders = useMemo(() => {
    if (!creds) return {};
    return {
      Authorization: `Basic ${window.btoa(`${creds.user}:${creds.pass}`)}`,
      "X-RiskaiA-Token": creds.token,
    };
  }, [creds]);

  useEffect(() => {
    if (!creds) return;
    let cancelled = false;
    setError("");
    fetch(`${API_BASE}/api/internal/documents/templates`, {
      headers: authHeaders,
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(await safeError(r));
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        const list = data.templates || [];
        setTemplates(list);
        if (list.length && !templateId) {
          // Honor a ?template= deep-link from the sales funnel (deal /
          // Gouden Standaard); fall back to the first template. A matching
          // sessionStorage prefill (client name etc., kept out of the URL
          // and logs) is merged over the defaults, then consumed once.
          const requested = new URLSearchParams(window.location.search).get("template");
          const chosen = list.find((t) => t.id === requested) || list[0];
          const prefill = takeOfferPrefill(chosen.id);
          setTemplateId(chosen.id);
          setMarkdown(chosen.starter_markdown || "");
          setFieldValues({ ...fieldDefaults(chosen), ...(prefill || {}) });
        }
      })
      .catch((e) => !cancelled && setError(e.message || "Failed to load templates."));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creds]);

  const onSaveCreds = (e) => {
    e.preventDefault();
    const f = e.target.elements;
    const next = {
      user: f.user.value.trim(),
      pass: f.pass.value,
      token: f.token.value.trim(),
    };
    if (!next.user || !next.pass || !next.token) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    setCreds(next);
  };

  const onClearCreds = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setCreds(null);
    setTemplates([]);
    setTemplateId("");
    setMarkdown("");
    setSuccessUrl("");
    setError("");
  };

  const currentTemplate = useMemo(
    () => templates.find((t) => t.id === templateId) || null,
    [templates, templateId],
  );

  const onPickTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    setMarkdown(t?.starter_markdown || "");
    setFieldValues(fieldDefaults(t));
    setSuccessUrl("");
  };

  const onResetMarkdown = () => {
    if (!currentTemplate) return;
    setMarkdown(currentTemplate.starter_markdown || "");
    setSuccessUrl("");
  };

  // Field helpers (scalars + list/table editors).
  const updField = (key, val) => setFieldValues((v) => ({ ...v, [key]: val }));
  const updListItem = (key, i, val) =>
    setFieldValues((v) => {
      const a = [...(v[key] || [])];
      a[i] = val;
      return { ...v, [key]: a };
    });
  const addListItem = (key) =>
    setFieldValues((v) => ({ ...v, [key]: [...(v[key] || []), ""] }));
  const removeListItem = (key, i) =>
    setFieldValues((v) => {
      const a = [...(v[key] || [])];
      a.splice(i, 1);
      return { ...v, [key]: a };
    });
  const updRowCell = (key, i, ck, val) =>
    setFieldValues((v) => {
      const a = [...(v[key] || [])];
      a[i] = { ...a[i], [ck]: val };
      return { ...v, [key]: a };
    });
  const addRow = (key, cols) =>
    setFieldValues((v) => ({
      ...v,
      [key]: [...(v[key] || []), Object.fromEntries(cols.map((c) => [c.key, c.default || ""]))],
    }));
  const removeRow = (key, i) =>
    setFieldValues((v) => {
      const a = [...(v[key] || [])];
      a.splice(i, 1);
      return { ...v, [key]: a };
    });
  const moveRow = (key, i, dir) =>
    setFieldValues((v) => {
      const a = [...(v[key] || [])];
      const j = i + dir;
      if (j < 0 || j >= a.length) return v;
      [a[i], a[j]] = [a[j], a[i]];
      return { ...v, [key]: a };
    });

  const [promptCopied, setPromptCopied] = useState(false);
  const [promptShown, setPromptShown] = useState(false);

  const onCopyPrompt = async () => {
    if (!currentTemplate?.ai_prompt) return;
    try {
      await navigator.clipboard.writeText(currentTemplate.ai_prompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch {
      setError("Kopiëren naar klembord mislukt. Selecteer en kopieer handmatig.");
      setPromptShown(true);
    }
  };

  const onGenerate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessUrl("");
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/internal/documents/generate-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({
          template: templateId,
          markdown,
          fields: fieldValues,
          ...(currentTemplate?.signatures ? { signatories } : {}),
        }),
      });
      if (!res.ok) throw new Error(await safeError(res));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Trigger download.
      const disposition = res.headers.get("Content-Disposition") || "";
      const filenameMatch = /filename="([^"]+)"/.exec(disposition);
      const filename = filenameMatch ? filenameMatch[1] : "riskaia-document.pdf";
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setSuccessUrl(filename);
    } catch (err) {
      setError(err.message || "PDF generation failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!creds) {
    return (
      <div data-testid="page-internal-documents">
        <section className="rk-container pt-20 md:pt-28 pb-10">
          <div className="overline mb-6">Intern · documentgenerator</div>
          <h1
            className="text-5xl md:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
          >
            Interne documentgenerator
          </h1>
          <p className="mt-8 text-lg text-[#D4D8DF] max-w-2xl leading-relaxed">
            Vul de Basic Auth-gegevens en de API-token in. Ze worden alleen
            lokaal in deze browser bewaard. De Basic Auth-gebruiker en
            -wachtwoord zijn dezelfde als waarmee je deze pagina hebt geopend.
          </p>
        </section>

        <div className="hairline" />

        <form
          onSubmit={onSaveCreds}
          className="rk-container py-12 md:py-16 max-w-xl space-y-6"
        >
          <label className="flex flex-col gap-2">
            <span className="overline">Basic Auth-gebruiker</span>
            <input id="user" name="user" type="text" className={inputCls} autoComplete="off" required />
          </label>
          <label className="flex flex-col gap-2">
            <span className="overline">Basic Auth-wachtwoord</span>
            <input id="pass" name="pass" type="password" className={inputCls} autoComplete="off" required />
          </label>
          <label className="flex flex-col gap-2">
            <span className="overline">Interne API-token</span>
            <input id="token" name="token" type="password" className={inputCls} autoComplete="off" required />
          </label>
          <button type="submit" className="rk-btn-primary">
            Doorgaan
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="page-internal-documents">
      <section className="rk-container pt-20 md:pt-28 pb-10">
        <button
          type="button"
          onClick={onClearCreds}
          className="overline mb-6 flex items-center gap-2 text-[#7FD7E4]"
        >
          Inloggegevens wissen
        </button>
        <div className="overline mb-6">Intern · documentgenerator</div>
        <h1
          className="text-5xl md:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl"
          style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
        >
          Interne documentgenerator
        </h1>
        <p className="mt-8 text-lg text-[#D4D8DF] max-w-2xl leading-relaxed">
          Genereer een gebrande RiskaiA-PDF vanuit Markdown. Alleen voor intern
          gebruik — gegenereerde PDF's worden nergens gepubliceerd en niet op de
          server bewaard.
        </p>
      </section>

      <div className="hairline" />

      <form onSubmit={onGenerate} className="rk-container py-12 md:py-16 space-y-10">
        {/* Document type */}
        <label className="flex flex-col gap-2 max-w-xl">
          <span className="overline">Documenttype</span>
          <select
            id="template"
            value={templateId}
            onChange={(e) => onPickTemplate(e.target.value)}
            className={inputCls}
          >
            {templates.length === 0 ? <option value="">— laden —</option> : null}
            {templates.map((t) => (
              <option key={t.id} value={t.id} className="text-[#0F1E33]">
                {t.label}
              </option>
            ))}
          </select>
        </label>

        {/* Document fields → YAML preamble (markdown) or values.yaml (edt) */}
        {currentTemplate?.fields?.length ? (
          <fieldset className="space-y-6">
            <legend className="overline mb-2">Documentgegevens</legend>

            {/* scalar fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {currentTemplate.fields
                .filter((f) => !["list", "table", "textarea", "boolean"].includes(f.type))
                .map((f) => (
                  <label key={f.key} className="flex flex-col gap-2">
                    <span className="overline">
                      {f.label}
                      {f.required ? " *" : ""}
                    </span>
                    <input
                      type={
                        f.type === "date"
                          ? "date"
                          : f.type === "price" || f.type === "number"
                          ? "number"
                          : "text"
                      }
                      step={f.type === "price" ? "0.01" : undefined}
                      value={fieldValues[f.key] ?? ""}
                      placeholder={f.placeholder || ""}
                      required={f.required}
                      onChange={(e) => updField(f.key, e.target.value)}
                      className={`${inputCls}${f.type === "date" ? " [color-scheme:dark]" : ""}`}
                      data-testid={`field-${f.key}`}
                    />
                  </label>
                ))}
            </div>

            {/* textarea fields (full width) */}
            {currentTemplate.fields
              .filter((f) => f.type === "textarea")
              .map((f) => (
                <label key={f.key} className="flex flex-col gap-2">
                  <span className="overline">
                    {f.label}
                    {f.required ? " *" : ""}
                  </span>
                  <textarea
                    value={fieldValues[f.key] ?? ""}
                    placeholder={f.placeholder || ""}
                    rows={4}
                    onChange={(e) => updField(f.key, e.target.value)}
                    className={areaCls}
                    data-testid={`field-${f.key}`}
                  />
                </label>
              ))}

            {/* boolean toggles */}
            {currentTemplate.fields.some((f) => f.type === "boolean") ? (
              <div className="flex flex-col gap-3">
                {currentTemplate.fields
                  .filter((f) => f.type === "boolean")
                  .map((f) => (
                    <label key={f.key} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!fieldValues[f.key]}
                        onChange={(e) => updField(f.key, e.target.checked)}
                        className="w-4 h-4 [color-scheme:dark]"
                        data-testid={`field-${f.key}`}
                      />
                      <span className="text-sm text-[#D4D8DF]">{f.label}</span>
                    </label>
                  ))}
              </div>
            ) : null}

            {/* list fields (e.g. frameworks) */}
            {currentTemplate.fields
              .filter((f) => f.type === "list")
              .map((f) => (
                <div key={f.key} className="space-y-2">
                  <span className="overline">{f.label}</span>
                  {(fieldValues[f.key] || []).map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <input
                        type="text"
                        value={item}
                        placeholder={f.item_label || ""}
                        onChange={(e) => updListItem(f.key, i, e.target.value)}
                        className={inputCls}
                      />
                      <button
                        type="button"
                        onClick={() => removeListItem(f.key, i)}
                        className="text-[#E8A0A0] shrink-0"
                        title="Verwijderen"
                      >
                        <X strokeWidth={1.5} className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addListItem(f.key)}
                    className="overline text-[#7FD7E4] flex items-center gap-2"
                  >
                    <Plus strokeWidth={1.5} className="w-3.5 h-3.5" /> {f.item_label || "Item"} toevoegen
                  </button>
                </div>
              ))}

            {/* table fields (e.g. posten) */}
            {currentTemplate.fields
              .filter((f) => f.type === "table")
              .map((f) => (
                <div key={f.key} className="space-y-3">
                  <span className="overline">{f.label}</span>
                  {(fieldValues[f.key] || []).map((row, i) => (
                    <div key={i} className="flex flex-wrap items-end gap-3">
                      {f.columns.map((c) => (
                        <label key={c.key} className="flex flex-col gap-1 grow">
                          <span className="text-[11px] uppercase tracking-[0.18em] text-[#9AA4B2]">
                            {c.label}
                          </span>
                          <input
                            type={c.type === "price" || c.type === "number" ? "number" : "text"}
                            step={c.type === "price" ? "0.01" : undefined}
                            value={row?.[c.key] ?? ""}
                            placeholder={c.placeholder || ""}
                            onChange={(e) => updRowCell(f.key, i, c.key, e.target.value)}
                            className={inputCls}
                          />
                        </label>
                      ))}
                      <div className="flex items-center gap-1 pb-2 shrink-0">
                        <button
                          type="button"
                          disabled={i === 0}
                          onClick={() => moveRow(f.key, i, -1)}
                          className="text-[#7FD7E4] disabled:opacity-30"
                          title="Omhoog"
                        >
                          <ChevronUp strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          disabled={i === (fieldValues[f.key] || []).length - 1}
                          onClick={() => moveRow(f.key, i, 1)}
                          className="text-[#7FD7E4] disabled:opacity-30"
                          title="Omlaag"
                        >
                          <ChevronDown strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRow(f.key, i)}
                          className="text-[#E8A0A0]"
                          title="Rij verwijderen"
                        >
                          <X strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addRow(f.key, f.columns)}
                    className="overline text-[#7FD7E4] flex items-center gap-2"
                  >
                    <Plus strokeWidth={1.5} className="w-3.5 h-3.5" /> Rij toevoegen
                  </button>
                </div>
              ))}

            {currentTemplate.kind !== "edt" ? (
              <p className="text-sm text-[#9AA4B2]">
                Deze gegevens vormen de documentkop; de AI schrijft alleen de body.
              </p>
            ) : null}
          </fieldset>
        ) : null}

        {/* AI prompt for the selected template (Markdown templates only) */}
        {currentTemplate?.kind !== "edt" && currentTemplate?.ai_prompt ? (
          <div className="border border-[#C0C5CE]/30 p-5">
            <div className="overline" style={{ color: "#7FD7E4" }}>
              AI-prompt voor dit template
            </div>
            <p className="mt-2 text-sm text-[#9AA4B2] max-w-2xl leading-relaxed">
              Kopieer en plak in ChatGPT / Claude. Vul de bracket-velden in; het
              antwoord plak je terug in het Markdown-veld hieronder.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-6">
              <button
                type="button"
                onClick={() => setPromptShown((v) => !v)}
                className="overline text-[#7FD7E4]"
              >
                {promptShown ? "Verberg prompt" : "Toon prompt"}
              </button>
              <button
                type="button"
                onClick={onCopyPrompt}
                className="overline text-[#7FD7E4]"
                data-testid="copy-ai-prompt"
              >
                {promptCopied ? "Gekopieerd ✓" : "Kopieer AI-prompt"}
              </button>
            </div>
            {promptShown ? (
              <textarea
                readOnly
                value={currentTemplate.ai_prompt}
                rows={14}
                spellCheck={false}
                className={`${areaCls} mt-4 font-mono text-xs leading-snug`}
                onFocus={(e) => e.target.select()}
              />
            ) : null}
          </div>
        ) : null}

        {/* Markdown body (Markdown templates only; EDT has no body) */}
        {currentTemplate?.kind !== "edt" ? (
        <label className="block">
          <div className="flex items-center justify-between mb-2">
            <span className="overline">Markdown-inhoud</span>
            <button
              type="button"
              onClick={onResetMarkdown}
              className="overline text-[#7FD7E4]"
            >
              Terug naar template
            </button>
          </div>
          <textarea
            id="markdown"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            rows={20}
            spellCheck={false}
            className={`${areaCls} font-mono text-sm leading-snug`}
          />
          <p className="mt-2 text-sm text-[#9AA4B2]">
            Tip: houd tabellen smal — gebruik bullet lists voor lange cellen.
            Datums in YAML in ISO-formaat (<code>YYYY-MM-DD</code>).
          </p>
        </label>
        ) : null}

        {/* Signatories (templates with a signature block) */}
        {currentTemplate?.signatures ? (
          <fieldset className="space-y-4">
            <legend className="overline mb-2">Ondertekenaars</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                ["one_role", "Rol partij 1"],
                ["one_name", "Naam & functie partij 1"],
                ["two_role", "Rol partij 2"],
                ["two_name", "Naam & functie partij 2"],
              ].map(([field, ph]) => (
                <input
                  key={field}
                  type="text"
                  value={signatories[field]}
                  placeholder={ph}
                  onChange={(e) =>
                    setSignatories((s) => ({ ...s, [field]: e.target.value }))
                  }
                  className={inputCls}
                  data-testid={`signatory-${field}`}
                />
              ))}
            </div>
            <p className="text-sm text-[#9AA4B2]">
              Verschijnen in het handtekenblok onderaan. Leeg laten houdt de
              standaard-placeholder aan.
            </p>
          </fieldset>
        ) : null}

        {/* Submit + status */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
          <div className="min-h-[1.5rem]">
            {error ? (
              <span className="flex items-center gap-2 text-[#E8A0A0]" role="alert">
                <AlertCircle strokeWidth={1.25} className="w-4 h-4" /> {error}
              </span>
            ) : successUrl ? (
              <span className="flex items-center gap-2 text-[#9FE2BF]">
                <CheckCircle2 strokeWidth={1.25} className="w-4 h-4" /> PDF
                gegenereerd: <code>{successUrl}</code> (download gestart).
              </span>
            ) : (
              <span className="text-sm text-[#9AA4B2]">
                De PDF wordt direct gedownload; er wordt geen kopie op de server
                bewaard.
              </span>
            )}
          </div>
          <button
            type="submit"
            disabled={busy || !templateId || (currentTemplate?.kind !== "edt" && !markdown.trim())}
            className="rk-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                Genereren… <Loader2 strokeWidth={1.25} className="w-4 h-4 animate-spin" />
              </>
            ) : (
              <>
                Genereer PDF <Download strokeWidth={1.25} className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

async function safeError(res) {
  try {
    const ct = res.headers.get("Content-Type") || "";
    if (ct.includes("application/json")) {
      const j = await res.json();
      return j.detail || JSON.stringify(j);
    }
    return (await res.text()) || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}
